
import { Injectable } from '@angular/core';
import { UserSettingsService, UserTableSetting } from '@root/src/app/core/services/user/user-settings.service';
import { UserSessionType } from '@root/src/app/core/model/user/session-type';
import { V2UserSetting } from '@bssh/ng-sdk';
import * as _ from 'lodash';
import { GridOptions } from 'ag-grid-community';
import { CustomColDef } from '@app/shared/bssh-data-table/custom-colDef';
import { BsshDataTableComponent } from '@app/shared/bssh-data-table/bssh-data-table.component';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { IColumnSetting, IColumnState, IUserGridSettings, findColDefForSetting } from './table-settings.models';

export { IColumnSetting, IColumnState, IUserGridSettings } from './table-settings.models';

@Injectable({
    providedIn: 'root'
})

export class TableSettingsService {
    constructor(private userSettingsService: UserSettingsService) { }

    public generateSelectionColumns(
        columnDefinitions: CustomColDef[]
    ): any[] {
        const selectionColumns: any[] = [];
        columnDefinitions
            .filter(col => !col.hide)
            .forEach(col => {
                selectionColumns.push({ colId: col.colId, state: true });
            });
        return selectionColumns;
    }

    public getColumnSettings(settingName: UserTableSetting, columnSettings: IColumnSetting[]): any {
        const setting = this.userSettingsService.getUserSetting(UserSessionType.LoggedInUser, settingName);
        if (setting) {
            const columnSelections = JSON.parse(setting.Value as string);
            // Get the columns which are marked 'showAlways' and transform them to objects to match the API response.
            const columnsToShowAlways = _.mapValues(_.keyBy(columnSettings.filter(x => x.showAlways === true), 'Id'), x => {
                return {
                    isVisible: true
                };
            });
            const merged = { ...columnSelections, ...columnsToShowAlways };

            // Self-heal: if any required column was saved as missing/false in the
            // persisted payload, re-save the corrected settings so the cloud
            // record no longer carries the bad state (BASE-88600).
            const requiredCols = columnSettings.filter(c => c.showAlways === true);
            const needsResave = requiredCols.some(c =>
                !columnSelections[c.Id] || columnSelections[c.Id].isVisible === false
            );
            if (needsResave) {
                this.saveRawSetting(settingName, merged);
            }

            return merged;
        }
    }

    public getGridSettings(settingName: UserTableSetting): IUserGridSettings {
        const setting = this.userSettingsService.getUserSetting(UserSessionType.LoggedInUser, settingName);
        if (setting) {
            return JSON.parse(setting.Value as string);
        } else {
            return {};
        }
    }

    public applyGridSettings(
        userSavedGridSettings: IUserGridSettings,
        gridOptions: GridOptions,
        dataTable?: BsshDataTableComponent
    ): void {
        if (userSavedGridSettings) {
            const { limit } = userSavedGridSettings;
            if(limit && gridOptions.api) {
              gridOptions.api.paginationSetPageSize(limit);
              if(dataTable) {
                dataTable.pageSizeController.updatePageStatus();
              }
            }
        }
    }

    public setGridSettings(settingName: string, gridSettings: IUserGridSettings): boolean {
        this.saveRawSetting(settingName, gridSettings);
        return true;
    }

    public setColumnSettings(settingName: UserTableSetting, columnSettings: IColumnSetting[], selectionColumns: any[]): boolean {
        const userSelection = this.getUserSelections(columnSettings, selectionColumns);
        if (userSelection) {
            this.saveRawSetting(settingName, userSelection);
            return true;
        }

    }

    public setGridColumnsVisibilityDefaults(
        columnSettings: IColumnSetting[],
        userSavedColumnSettings: any,
        gridOptions: GridOptions,
        columnDefinitions: CustomColDef[]
    ): void {
        if (userSavedColumnSettings) {
            let keyValuePairsColumn: string[] = [];
            keyValuePairsColumn = Object.keys(userSavedColumnSettings);

            columnDefinitions.forEach(col => { col.hide = true; });

            keyValuePairsColumn.forEach(key => {

                const columnSetting = columnSettings.find(x => x.Id === key);
                if (columnSetting) {
                    const col = findColDefForSetting(columnSetting, columnDefinitions);
                    if (col) {
                        col.hide = false;
                    }
                }
            });
            if (gridOptions.api) {
                gridOptions.api.setColumnDefs([]);
                gridOptions.api.setColumnDefs(columnDefinitions);
                gridOptions.api.sizeColumnsToFit();
            }
        }
    }

    /**
     * Persists a JSON-serializable value as a user setting under the given name.
     */
    private saveRawSetting(settingName: string, value: any): void {
        const setting: V2UserSetting = {
            Id: null,
            Href: BsApiEndPoints.LOGGEDIN_USER_SETTINGS_URL,
            Name: settingName,
            Value: JSON.stringify(value),
            Type: 'string'
        };
        this.userSettingsService.setUserSettings(UserSessionType.LoggedInUser, [setting]);
    }

    private getUserSelections(columnSettings: IColumnSetting[], selectionColumns: any[]): any {
        const keyValuePairs: [string, IColumnState][] = [];
        selectionColumns.forEach(col => {
            const column = columnSettings.find(item => item.label === col.colId);
            if (column) {
                const state: IColumnState = {
                    isVisible: true
                };
                keyValuePairs.push([column.Id, state ? state : null]);
            }
        });
        const result = _.fromPairs(keyValuePairs);

        // Defense-in-depth: always emit required (`showAlways`) columns as
        // visible, even if they were missing from `selectionColumns` (which
        // can happen if a switcher bug hides them or a consumer forgets to
        // include them). This prevents BASE-88600-style bad state from being
        // written to the cloud user-setting.
        columnSettings
            .filter(c => c.showAlways === true)
            .forEach(c => {
                result[c.Id] = { isVisible: true };
            });

        return result;
    }
}
